import { getPrefectureName, PREFECTURE_OPTIONS } from '@admission/shared';
import { ArrowRight, ArrowUpDown, Building2, Check, ChevronDown, ChevronUp, Search, X } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';

import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { apiUrl } from '@/config/api';
import { authGet } from '@/lib/authFetch';
import { cn } from '@/lib/utils';

// Corporation Interface
interface Corporation {
  id: string; // Corporation Number (13 digits)
  name: string;
  nameKana?: string;
  representativeName?: string;
  address?: string;
  prefectureCode?: string | null;
  addressText?: string | null;
  chairmanName?: string | null;
  secretaryGeneralName?: string | null;
  createdAt: string;
  // Stats or other fields
  status: 'active' | 'suspended' | 'deleted';
  isContracted: 'true' | 'false';
  // Linked institutions
  institutions?: Array<{
    id: string;
    name: string;
    type: 'national' | 'public' | 'private';
    schoolType: 'university' | 'junior_college' | 'kosen' | 'graduate_school';
  }>;
}

type SortField = 'name' | 'id' | 'createdAt';
type SortOrder = 'asc' | 'desc';

interface MasterOrganization {
  id: string;
  name: string;
  type: string;
  establishmentClass?: string;
  abolitionDate?: string;
  mergerDate?: string;
  mergerTargetId?: string;
}

const getTenantTypeStyles = (type: string) => {
  switch (type?.toLowerCase()) {
    case 'national':
      return 'bg-blue-50 text-blue-700 border-blue-200';
    case 'public':
      return 'bg-green-50 text-green-700 border-green-200';
    case 'private':
      return 'bg-purple-50 text-purple-700 border-purple-200';
    default:
      return 'bg-gray-50 text-gray-700 border-gray-200';
  }
};

const getCandidateTypeStyles = (type: string) => getTenantTypeStyles(type);

export default function TenantList() {
  const { t } = useTranslation();
  const [activeCorporations, setActiveCorporations] = useState<Corporation[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Filter & UI State
  // filterMode: 'contracted' | 'notContracted'
  const [filterMode, setFilterMode] = useState<'contracted' | 'notContracted'>('contracted');
  const [listSearch, setListSearch] = useState('');
  const [filterPrefecture, setFilterPrefecture] = useState('');
  const [filterType, setFilterType] = useState(''); // national, public, private
  const [filterSchoolType, setFilterSchoolType] = useState(''); // university, junior_college, kosen, graduate_school

  // Sorting State
  const [sortField, setSortField] = useState<SortField>('createdAt');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');

  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const PAGE_SIZES = [10, 20, 50, 100];

  // Modal State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<MasterOrganization[]>([]);
  const [selectedCandidate, setSelectedCandidate] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    try {
      const res = await authGet(apiUrl('/api/tenants'));
      if (res.ok) {
        const data = (await res.json()) as Corporation[];
        setActiveCorporations(data);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  // Count contracted and not contracted corporations
  const contractedCount = useMemo(
    () => activeCorporations.filter((t) => t.isContracted === 'true').length,
    [activeCorporations],
  );
  const notContractedCount = useMemo(
    () => activeCorporations.filter((t) => t.isContracted !== 'true').length,
    [activeCorporations],
  );

  const filteredAndSorted = useMemo(() => {
    const filtered = activeCorporations.filter((t) => {
      // Contract Status Filter
      if (filterMode === 'contracted' && t.isContracted !== 'true') return false;
      if (filterMode === 'notContracted' && t.isContracted === 'true') return false;
      // 'all' shows everything

      // Prefecture Filter — check both tenant and child institution prefectures
      if (filterPrefecture) {
        const tenantMatch = t.prefectureCode === filterPrefecture;
        const instMatch = t.institutions?.some((inst) => {
          // Extract prefecture from MEXT code position 2-3
          const instPref = inst.id?.length >= 4 ? inst.id.substring(2, 4) : null;
          return instPref === filterPrefecture;
        });
        if (!tenantMatch && !instMatch) return false;
      }

      // Institution Type Filter (national, public, private)
      if (filterType && t.institutions) {
        const hasMatchingType = t.institutions.some((inst) => inst.type === filterType);
        if (!hasMatchingType) return false;
      } else if (filterType && !t.institutions?.length) {
        return false;
      }

      // School Type Filter (university, junior_college, kosen, graduate_school)
      if (filterSchoolType && t.institutions) {
        const hasMatchingSchoolType = t.institutions.some((inst) => inst.schoolType === filterSchoolType);
        if (!hasMatchingSchoolType) return false;
      } else if (filterSchoolType && !t.institutions?.length) {
        return false;
      }

      // Text Search
      if (listSearch) {
        const searchLower = listSearch.toLowerCase();
        const matchesName = t.name.toLowerCase().includes(searchLower);
        const matchesId = t.id.includes(searchLower);
        // Also search in institution names
        const matchesInstitution = t.institutions?.some((inst) => inst.name.toLowerCase().includes(searchLower));
        if (!matchesName && !matchesId && !matchesInstitution) return false;
      }
      return true;
    });

    return [...filtered].sort((a, b) => {
      let comparison = 0;
      if (sortField === 'name') {
        comparison = (a.nameKana || a.name).localeCompare(b.nameKana || b.name, 'ja');
      } else if (sortField === 'createdAt') {
        comparison = a.createdAt.localeCompare(b.createdAt);
      } else {
        const key = sortField as keyof Corporation;
        const valA = String(a[key] || '');
        const valB = String(b[key] || '');
        comparison = valA.localeCompare(valB);
      }
      return sortOrder === 'asc' ? comparison : -comparison;
    });
  }, [
    activeCorporations,
    listSearch,
    sortField,
    sortOrder,
    filterMode,
    filterPrefecture,
    filterType,
    filterSchoolType,
  ]);

  const totalItems = filteredAndSorted.length;
  const totalPages = Math.ceil(totalItems / pageSize);
  const paginatedItems = filteredAndSorted.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  // Search effect for modal
  useEffect(() => {
    const delayDebounceFn = setTimeout(async () => {
      if (searchTerm) {
        try {
          const params = new URLSearchParams();
          params.append('q', searchTerm);

          const res = await authGet(apiUrl(`/api/master/organizations?${params.toString()}`));
          if (res.ok) {
            setSearchResults((await res.json()) as MasterOrganization[]);
          }
        } catch (e) {
          console.error(e);
        }
      } else {
        setSearchResults([]);
      }
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchTerm]);

  if (isLoading) {
    return <div className="p-8 text-center text-gray-500">{t('common.loading')}</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2 text-gray-900">
            <Building2 className="w-7 h-7 text-gray-700" />
            {t('tenants.title')}
          </h1>
          <p className="text-gray-500 mt-1">{t('tenants.description')}</p>
        </div>
        <Button asChild className="gap-2">
          <Link to="/system/tenants/create">{t('tenants.buttons.create')}</Link>
        </Button>
      </div>

      <div className="bg-white p-4 border border-gray-200 rounded-lg shadow-sm space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder={t('tenants.filters.searchPlaceholder')}
              value={listSearch}
              onChange={(e) => setListSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none"
            />
          </div>
          <select
            value={filterPrefecture}
            onChange={(e) => {
              setFilterPrefecture(e.target.value);
              setCurrentPage(1);
            }}
            className="w-full px-4 py-2 border border-gray-200 rounded-lg text-sm outline-none"
          >
            <option value="">{t('tenants.filters.allPrefectures')}</option>
            {PREFECTURE_OPTIONS.map(({ code }) => (
              <option key={code} value={code}>
                {t(`tenants.prefectures.${code}`)}
              </option>
            ))}
          </select>
          <select
            value={filterType}
            onChange={(e) => {
              setFilterType(e.target.value);
              setCurrentPage(1);
            }}
            className="w-full px-4 py-2 border border-gray-200 rounded-lg text-sm outline-none"
          >
            <option value="">{t('tenants.filters.allTypes')}</option>
            <option value="national">{t('tenants.types.national')}</option>
            <option value="public">{t('tenants.types.public')}</option>
            <option value="private">{t('tenants.types.private')}</option>
          </select>
          <select
            value={filterSchoolType}
            onChange={(e) => {
              setFilterSchoolType(e.target.value);
              setCurrentPage(1);
            }}
            className="w-full px-4 py-2 border border-gray-200 rounded-lg text-sm outline-none"
          >
            <option value="">{t('tenants.filters.allSchoolTypes')}</option>
            <option value="university">{t('tenants.schoolTypes.university')}</option>
            <option value="junior_college">{t('tenants.schoolTypes.juniorCollege')}</option>
            <option value="kosen">{t('tenants.schoolTypes.kosen')}</option>
            <option value="graduate_school">{t('tenants.schoolTypes.graduateSchool')}</option>
          </select>
        </div>
      </div>

      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => {
            setFilterMode('contracted');
            setCurrentPage(1);
          }}
          className={`px-4 py-1.5 text-xs font-bold rounded-md transition-all ${
            filterMode === 'contracted' ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          {t('tenants.tabs.contracted')} ({contractedCount})
        </button>
        <button
          type="button"
          onClick={() => {
            setFilterMode('notContracted');
            setCurrentPage(1);
          }}
          className={`px-4 py-1.5 text-xs font-bold rounded-md transition-all ${
            filterMode === 'notContracted' ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          {t('tenants.tabs.notContracted')} ({notContractedCount})
        </button>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm">
        <Table>
          <TableHeader>
            <TableRow className="bg-gray-50/50">
              <TableHead onClick={() => handleSort('name')} className="cursor-pointer group">
                <div className="flex items-center gap-1">
                  {t('tenants.table.corporationName')}
                  <SortIcon field="name" currentField={sortField} order={sortOrder} />
                </div>
              </TableHead>
              <TableHead onClick={() => handleSort('id')} className="cursor-pointer group">
                <div className="flex items-center gap-1">
                  {t('tenants.table.corporateNumber')}
                  <SortIcon field="id" currentField={sortField} order={sortOrder} />
                </div>
              </TableHead>
              <TableHead>{t('tenants.table.representative')}</TableHead>
              <TableHead>{t('tenants.table.location')}</TableHead>
              <TableHead className="text-right">{t('tenants.table.actions')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {paginatedItems.map((corp) => (
              <TableRow key={corp.id} className="hover:bg-gray-50/50 transition-colors">
                <TableCell className="py-4 font-bold text-gray-900">
                  <div className="hover:text-blue-600 block cursor-default">{corp.name}</div>
                  <span className="text-xs text-gray-400 font-normal">{corp.nameKana}</span>
                </TableCell>
                <TableCell className="font-mono text-gray-600">{corp.id}</TableCell>
                <TableCell>{corp.chairmanName || '---'}</TableCell>
                <TableCell className="text-sm text-gray-600" title={corp.addressText || ''}>
                  {corp.prefectureCode ? getPrefectureName(corp.prefectureCode) : '---'}
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-2">
                    <Button variant="ghost" size="sm" asChild>
                      <Link to={`/system/tenants/${corp.id}`}>{t('tenants.buttons.detail')}</Link>
                    </Button>
                    {filterMode === 'notContracted' && (
                      <Button size="sm" asChild>
                        <Link to={`/system/tenants/wizard?id=${corp.id}&name=${encodeURIComponent(corp.name)}`}>
                          {t('tenants.buttons.startContract')}
                        </Link>
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {paginatedItems.length === 0 && (
              <TableRow>
                <TableCell colSpan={4} className="text-center py-12 text-gray-500">
                  {t('tenants.noData.all')}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>

        <div className="p-4 border-t border-gray-200 flex items-center justify-between bg-gray-50">
          <div className="text-sm text-gray-600">{t('tenants.pagination.total', { count: totalItems })}</div>
          <div className="flex items-center gap-4">
            <select
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setCurrentPage(1);
              }}
              className="border border-gray-300 rounded px-2 py-1 text-sm bg-white outline-none"
            >
              {PAGE_SIZES.map((size) => (
                <option key={size} value={size}>
                  {t('tenants.pagination.perPage', { count: size })}
                </option>
              ))}
            </select>
            <div className="flex items-center gap-1">
              <Button
                variant="outline"
                size="sm"
                className="h-8"
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
              >
                {t('common.previous')}
              </Button>
              <span className="px-3 py-1 text-sm font-medium">
                {currentPage} / {totalPages || 1}
              </span>
              <Button
                variant="outline"
                size="sm"
                className="h-8"
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage >= totalPages}
              >
                {t('common.next')}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl overflow-hidden flex flex-col max-h-[85vh]">
            <div className="p-4 border-b border-gray-100 flex items-center justify-between bg-gray-50/50">
              <h2 className="font-semibold text-lg text-gray-800">{t('tenants.modal.addPartner')}</h2>
              <button
                type="button"
                onClick={() => setIsModalOpen(false)}
                className="text-gray-400 hover:text-gray-600 p-1 hover:bg-gray-200 rounded-full transition"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-4 border-b border-gray-100 space-y-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder={t('tenants.modal.searchPlaceholder') || 'Search by Name or Code...'}
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none transition-all"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-4 bg-gray-50/30">
              <div className="space-y-2">
                {searchResults.map((univ) => (
                  <button
                    type="button"
                    key={univ.id}
                    onClick={() => setSelectedCandidate(univ.id)}
                    className={cn(
                      'w-full p-4 rounded-lg border text-left transition-all flex items-center justify-between group',
                      selectedCandidate === univ.id
                        ? 'bg-blue-50 border-blue-500 ring-1 ring-blue-500'
                        : 'bg-white border-gray-200 hover:border-blue-300',
                    )}
                  >
                    <div>
                      <div className="font-bold text-gray-900 flex items-center gap-2">
                        {univ.name}
                        <span
                          className={cn(
                            'text-[10px] px-1.5 py-0.5 rounded border uppercase',
                            getCandidateTypeStyles(univ.type),
                          )}
                        >
                          {univ.type}
                        </span>
                      </div>
                    </div>
                    <div
                      className={cn(
                        'w-6 h-6 rounded-full flex items-center justify-center border transition-colors',
                        selectedCandidate === univ.id
                          ? 'bg-blue-600 border-blue-600 text-white'
                          : 'border-gray-300 text-transparent group-hover:border-blue-400',
                      )}
                    >
                      <Check className="w-4 h-4" />
                    </div>
                  </button>
                ))}
                {searchTerm && searchResults.length === 0 && (
                  <div className="text-center py-12 text-gray-500">{t('tenants.modal.noResults')}</div>
                )}
                {!searchTerm && <div className="text-center py-12 text-gray-400">{t('tenants.modal.startTyping')}</div>}
              </div>
            </div>

            <div className="p-4 border-t border-gray-100 bg-white flex justify-end gap-3">
              <Button variant="ghost" onClick={() => setIsModalOpen(false)}>
                {t('tenants.modal.cancel')}
              </Button>
              <Button disabled={!selectedCandidate} asChild={!!selectedCandidate} className="font-bold gap-2 px-8">
                {selectedCandidate ? (
                  <Link
                    to={`/system/tenants/create?mextCode=${selectedCandidate}&name=${encodeURIComponent(searchResults.find((r) => r.id === selectedCandidate)?.name || '')}`}
                  >
                    {t('tenants.modal.continue')} <ArrowRight className="w-4 h-4" />
                  </Link>
                ) : (
                  <span>
                    {t('tenants.modal.continue')} <ArrowRight className="w-4 h-4" />
                  </span>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function SortIcon({
  field,
  currentField,
  order,
}: Readonly<{ field: SortField; currentField: SortField; order: SortOrder }>) {
  if (currentField !== field)
    return <ArrowUpDown className="w-3 h-3 text-gray-300 opacity-0 group-hover:opacity-100 transition-opacity" />;
  return order === 'asc' ? (
    <ChevronUp className="w-3 h-3 text-blue-600" />
  ) : (
    <ChevronDown className="w-3 h-3 text-blue-600" />
  );
}
